'use client';

/**
 * useFetchGwpName
 *
 * Fetches the localized gift name for a GWP product via its
 * `com-gwp-name` attribute translation. Falls back to product.name
 * when no translation exists.
 */

import { useEffect, useState } from 'react';

import { devError } from '@/lib/utils/devLog';

interface UseFetchGwpNameParams {
  productId: string | null;
  channel: string | null;
  languageCode: string | null;
}

export function useFetchGwpName({
  productId,
  channel,
  languageCode,
}: UseFetchGwpNameParams): string | null {
  const [name, setName] = useState<string | null>(null);

  useEffect(() => {
    if (!productId || !channel || !languageCode) {
      setName(null);
      return;
    }

    let cancelled = false;

    const fetchName = async () => {
      try {
        const params = new URLSearchParams({
          id: productId,
          channel,
          languageCode,
        });

        const response = await fetch(`/api/checkout/gwp/details?${params}`);

        if (cancelled || !response.ok) return;

        const json = await response.json();
        if (!cancelled) {
          setName(json.data?.name ?? null);
        }
      } catch (err) {
        devError('[GWP] Failed to fetch gift name:', err);
      }
    };

    fetchName();

    return () => {
      cancelled = true;
    };
  }, [productId, channel, languageCode]);

  return name;
}
